import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import Loader from "../components/Loader";
import LocationRange from "../components/map/LocationRange";
import ProfileImages from "../components/profile/ProfileImages";
import { editProfile, setAlertMessage } from "../redux/authSlice";
import { subscribePush, unsubscribePush } from "../redux/pushSubscriptionSlice";
import "../style/Profile.css";
import { FormGroup, Checkbox } from "@mui/material";
import {
  askNotificationPermission,
  emitSampleNotification,
  registerServiceWorker,
  urlBase64ToUint8Array,
} from "../lib/pushNotification";

const years = [];
for (
  let year = new Date().getFullYear() - 16;
  year >= new Date().getFullYear() - 100;
  year -= 1
) {
  years.push({
    value: year,
    label: year,
  });
}

const getBrowserLocation = () => {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        (error) => {
          reject(error);
        }
      );
    } else {
      reject(new Error("Geolocation is not supported by this browser."));
    }
  });
};

function Profile() {
  const { t, i18n } = useTranslation();
  const { allLanguages: languages } = i18n;
  const { status, isLoggedIn, profile } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [username, setUsername] = useState("");
  const [gender, setGender] = useState("");
  const [defaultLanguage, setDefaultLanguage] = useState("");
  const [yearOfBirth, setYearOfBirth] = useState("");
  const [interestRange, setInterestRange] = useState(10);
  const [defaultLocation, setDefaultLocation] = useState({});
  const [profilePic, setProfilePic] = useState({});
  const [selectedLabels, setSelectedLabels] = useState({});
  const ipLocation = { ...profile.ipLocation };
  const [notificationPreferences, setNotificationPreferences] =
    useState(null);

  // email subscription preferences
  // Initialize notification checkboxes from emailPreferences array
  const [isNotifyForMessage, setIsNotifyForMessage] = useState(
    profile?.emailPreferences?.includes("updates") ?? false
  );
  const [isGeneralNotification, setIsGeneralNotification] = useState(
    profile?.emailPreferences?.includes("newsletters") ?? false
  );


  // keep the states sync
  const hasRunRef = useRef(false);
  useEffect(() => {
    if (hasRunRef.current) return;
    console.log('current logged in profile:', profile);
    setUsername(profile.username);
    setGender(profile.gender || "male");
    setDefaultLanguage(
      profile.defaultLanguage || localStorage.getItem("i18nextLng")
    );
    setYearOfBirth(profile.yearOfBirth || years[0].value);
    setDefaultLocation({ ...profile.defaultLocation } || {});
    setInterestRange(profile.interestRange || 10);
    setProfilePic(profile.profilePic);
    setSelectedLabels(profile?.labels || {});
    setNotificationPreferences(profile.notificationPreferences || "email");
    
    setIsNotifyForMessage(profile?.emailPreferences?.includes("updates") ?? false);
    setIsGeneralNotification(profile?.emailPreferences?.includes("newsletters") ?? false);
    hasRunRef.current = true;
  }, [profile]);

  const [location, setLocation] = useState({ lat: 48.8584, lng: 2.2945 });

  useEffect(() => {
    if (!hasRunRef.current || !profile._id) {
      return;
    }
    if (location?.setByUser) return;
    const handleBrowserLocation = async () => {
      if (!profile?.defaultLocation.lat || !profile?.defaultLocation.lng) {
        if (ipLocation?.lat && ipLocation?.lng) {
          setLocation({ ...ipLocation, setByUser: true });
        } else {
          try {
            const browserLocation = await getBrowserLocation();
            if (browserLocation?.lat && browserLocation?.lng) {
              setLocation({ ...browserLocation, setByUser: true });
            } else {
              setLocation({ lat: 48.8584, lng: 2.2945 });
            }
          } catch (error) {
            console.error("Error getting location:", error);
          }
        }
      } else {
        setLocation((prev) => {
          if (
            prev?.lat !== profile.defaultLocation.lat &&
            prev?.lng !== profile.defaultLocation.lng
          ) {
            return profile.defaultLocation;
          } else {
            return prev;
          }
        });
      }
    };

    handleBrowserLocation();
  }, [profile, ipLocation]);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const canSubmit = useRef(false);

  const handleSubmit = useCallback(
    async (e) => {
      if (!canSubmit.current) {
        canSubmit.current = !canSubmit.current;
        return null;
      }
      if (e) e.preventDefault();
      if (!isFormDirty) return null;
      if (!username)
        return dispatch(
          setAlertMessage({
            message: t("usernameRequired"),
            show: true,
            type: "error",
          })
        );
      // if (!email)
      //     return dispatch(
      //         setAlertMessage({ message: t('emailRequired'), show: true, type: 'error' })
      //     );
      if (!location)
        return dispatch(
          setAlertMessage({
            message: t("locationRequired"),
            show: true,
            type: "error",
          })
        );
      console.log("Submitting profile updates...", isGeneralNotification, isNotifyForMessage);
      const emailPreferences = [];
      if (isGeneralNotification) emailPreferences.push("newsletters");
      if (isNotifyForMessage) emailPreferences.push("updates");

      const updates = {
        id: profile._id,
        updates: {
          username,
          gender,
          yearOfBirth,
          profilePic,
          defaultLocation: { lat: location.lat, lng: location.lng },
          defaultLanguage,
          interestRange,
          labels: selectedLabels,
          notificationPreferences,
          emailPreferences,
        },
      };
      return dispatch(editProfile(updates));
    },
    [
      defaultLanguage,
      profilePic,
      gender,
      interestRange,
      isFormDirty,
      location,
      profile._id,
      t,
      selectedLabels,
      username,
      yearOfBirth,
      notificationPreferences,
      isGeneralNotification,
      isNotifyForMessage,
      dispatch,
    ]
  );
  const handleLanguageChange = async (lang) => {
    setIsFormDirty(true);
    setDefaultLanguage(lang);
    await handleSubmit(null, () => { });
    localStorage.setItem("i18nextLng", lang);
  };
  const handleChange = (e, setter) => {
    setter(e.target.value);
    setIsFormDirty(true);
  };

  const handleNotificationPreferenceChange = async (e) => {
    const nextValue = e.target.value;
    setNotificationPreferences(nextValue);
    setIsFormDirty(true);

    if (nextValue === "browser" && !browserNotificationEnabled) {
      await handleBrowserNotificationToggleChange();
    }
  };
  const [browserNotificationEnabled, setBrowserNotificationEnabled] = useState(false);
  const [showBrowserNotificationHint, setShowBrowserNotificationHint] = useState(false);
  // check if browser notifications are enabled
  useEffect(() => {
     const checkSubscription = async () => {
      if (Notification.permission !== "granted") {
        setBrowserNotificationEnabled(false);
        return;
      }

      if (!("serviceWorker" in navigator)) {
        setBrowserNotificationEnabled(false);
        return;
      }

      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setBrowserNotificationEnabled(Boolean(subscription));
      } catch (error) {
        setBrowserNotificationEnabled(false);
      }
    };

    checkSubscription();
  }, []);

  const disableBrowserNotifications = useCallback(async () => {
    setBrowserNotificationEnabled(false);

    if (!("serviceWorker" in navigator)) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await dispatch(
          unsubscribePush({ endpoint: subscription.endpoint, silent: true })
        );
        await subscription.unsubscribe();
      }
    } catch (error) {
      // best-effort; user can also disable from browser settings
    }
  }, [dispatch]);

  useEffect(() => {
    // Only show a hint; permission prompts must be triggered by a user action (button click).
    const wantsBrowserNotifications =
      notificationPreferences === "browser";

    setShowBrowserNotificationHint(
      Boolean(wantsBrowserNotifications && !browserNotificationEnabled)
    );
  }, [notificationPreferences, browserNotificationEnabled]);

 useEffect(() => {
    if (notificationPreferences === "email") {
      disableBrowserNotifications();
    }
  }, [notificationPreferences, disableBrowserNotifications]);

  // browser notification permission
  const handleBrowserNotificationToggleChange = async () => {
    console.log("Toggling browser notifications. Current state:", {
      notificationPreferences,
      browserNotificationEnabled,
    });
    try {
      if (!("Notification" in window)) {
        dispatch(
          setAlertMessage({
            message: t("browserNotificationError"),
            show: true,
            type: "error",
          })
        );
        return;
      }
      if (!("serviceWorker" in navigator)) {
        dispatch(
          setAlertMessage({
            message: t("browserNotificationError"),
            show: true,
            type: "error",
          })
        );
        return;
      }
      console.log("Registering service worker...");
      const registration = await registerServiceWorker();
      const grantedPermission =
        Notification.permission === "granted"
          ? true
          : await askNotificationPermission();
      if (grantedPermission) {
        setBrowserNotificationEnabled(true);
        // Subscribe to push notifications
        const swRegistration =
          registration || (await navigator.serviceWorker.ready);
        const publicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
        const applicationServerKey = urlBase64ToUint8Array(publicKey);
        if (!applicationServerKey) {
          throw new Error("Missing or invalid VAPID public key");
        }
        const existingSubscription =
          await swRegistration.pushManager.getSubscription();
        const subscription =
          existingSubscription ||
          (await swRegistration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
          }));
        await dispatch(subscribePush(subscription.toJSON()));
        dispatch(
          setAlertMessage({
            message: t("browserNotificationEnabled"),
            show: true,
            type: "success",
          })
        );
        await emitSampleNotification();
        console.log("Browser notifications enabled.");
      } else {
        console.log("Browser notification permission denied.");
        setBrowserNotificationEnabled(false);
        // Unsubscribe if permission is denied
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await dispatch(unsubscribePush(subscription.endpoint));
          await subscription.unsubscribe();
        }
        console.log("Browser notifications disabled due to permission denial.");
        dispatch(
          setAlertMessage({
            message: t("browserNotificationDenied"),
            show: true,
            type: "info",
          })
        );
      }
    } catch (error) {
      console.log("Error toggling browser notifications:", error);
      setBrowserNotificationEnabled(false);
      console.error("Error enabling browser notifications:", error);
      dispatch(
        setAlertMessage({
          message: t("browserNotificationError"),
          show: true,
          type: "error",
        })
      );
    }
  };

  // change the language after submission
  useEffect(() => {
    if (i18n.language === profile.defaultLanguage) return;
    i18n.changeLanguage(profile.defaultLanguage);
  }, [profile.defaultLanguage, i18n]);

  // delay the submission when the range value changes to limit api calls
  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) {
      // Skip the initial call when the component is mounted
      isMounted.current = true;
      return;
    }
    setIsFormDirty(true);

    const debounceTimeout = setTimeout(() => {
      handleSubmit();
    }, 1000);

    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [location, interestRange, handleSubmit]);

  return (
    <Box className="w-full" alignSelf="center">
      <Paper
        className="max-w-[1080px] w-full mx-auto"
        elevation={3}
        sx={{
          padding: "2rem 1rem",
          my: 2,
          borderRadius: 3,
        }}
      >
        {status === "success" && isLoggedIn ? (
          <>
            <Typography variant="h4" component="h1" textAlign="center" my={2}>
              {t("profileSettings")}
            </Typography>
            <ProfileImages
              profile={profile}
              t={t}
              selectedLabels={selectedLabels}
              setSelectedLabels={setSelectedLabels}
              dispatch={dispatch}
              updateLabels={handleSubmit}
            />
            <form onSubmit={handleSubmit}>
              <TextField
                id="username"
                label={t("username")}
                name="username"
                variant="outlined"
                sx={{ width: "100%", my: 1 }}
                value={username}
                InputLabelProps={{ shrink: !!username }}
                onChange={(e) => handleChange(e, setUsername)}
              />
              <Box sx={{ display: "flex", flexWrap: "nowrap" }}>
                <TextField
                  id="email"
                  label={t("email")}
                  name="email"
                  variant="outlined"
                  sx={{ width: "100%", my: 1 }}
                  value={profile?.email}
                  disabled
                  InputLabelProps={{ shrink: !!profile?.email }}
                />
                <Button
                  variant="outlined"
                  color="inherit"
                  sx={{ my: 1, ml: 1 }}
                  to="/change-email"
                  component={NavLink}
                >
                  {t("edit")}
                </Button>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                }}
              >
                <FormControl sx={{ width: "100%", my: 1, mr: 1 }}>
                  <InputLabel id="yob-label">{t("yearOfBirth")}</InputLabel>
                  <Select
                    labelId="yob-label"
                    id="yob"
                    value={yearOfBirth}
                    label={t("yearOfBirth")}
                    onChange={(e) => handleChange(e, setYearOfBirth)}
                  >
                    {years.map((year) => (
                      <MenuItem key={year.value} value={year.value}>
                        {year.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth sx={{ width: "100%", my: 1, ml: 1 }}>
                  <InputLabel id="gender-label">{t("gender")}</InputLabel>
                  <Select
                    labelId="gender-label"
                    id="gender"
                    value={gender}
                    label={t("gender")}
                    onChange={(e) => handleChange(e, setGender)}
                  >
                    <MenuItem value="male" selected>
                      {t("male")}
                    </MenuItem>
                    <MenuItem value="female">{t("female")}</MenuItem>
                    <MenuItem value="other">{t("other")}</MenuItem>
                    <MenuItem value="undisclosed">{t("Undisclosed")}</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <FormControl fullWidth sx={{ width: "100%", my: 1 }}>
                <InputLabel id="language-label">
                  {t("defaultLanguage")}
                </InputLabel>
                <Select
                  labelId="language-label"
                  id="defaultLanguage"
                  value={defaultLanguage}
                  label={t("defaultLanguage")}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                >
                  {languages.map((lan) => (
                    <MenuItem key={lan.code} value={lan.code}>
                      {lan.nativeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {profile._id && (
                <LocationRange
                  interestRange={interestRange}
                  setInterestRange={setInterestRange}
                  location={location}
                  defaultLocation={defaultLocation}
                  setLocation={setLocation}
                  ipLocation={ipLocation}
                />
              )}
              <FormControl sx={{ my: 1 }}>
                <FormLabel id="notification-preferences-label">
                  {t("notificationPreferences")}
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="notification-preferences-label"
                  name="notification-preferences"
                  value={notificationPreferences}
                  onChange={handleNotificationPreferenceChange}
                >
                  <FormControlLabel
                    value="email"
                    control={<Radio />}
                    label={t("email")}
                  />
                  <FormControlLabel
                    value="browser"
                    control={<Radio />}
                    label={t("browser")}
                  />
                </RadioGroup>
                <Alert severity="info">{t("lessEmailNotification")}</Alert>
              </FormControl>

                  {(
                    notificationPreferences === "email" &&
                    <FormGroup sx={{ ml: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isNotifyForMessage}
                          onChange={(e) => {
                            setIsNotifyForMessage(e.target.checked);
                            setIsFormDirty(true);
                          }}
                        />
                      }
                      label={t("notifyForMessageAndSubscribedContent")}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isGeneralNotification}
                          onChange={(e) => {
                            setIsGeneralNotification(e.target.checked);
                            setIsFormDirty(true);
                          }}
                        />
                      }
                      label={t("sendGeneralNotification")}
                    />
                  </FormGroup>
                  )}

                  { ( notificationPreferences === "browser"
                     && !browserNotificationEnabled) && <FormControl fullWidth sx={{ width: "100%", my: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography>{t("enableBrowserNotifications")}</Typography>
                      <Button
                        variant={
                          browserNotificationEnabled &&
                          notificationPreferences === "browser"
                            ? "contained"
                            : "outlined"
                        }
                        color={
                          browserNotificationEnabled &&
                          notificationPreferences === "browser"
                            ? "success"
                            : "primary"
                        }
                        onClick={handleBrowserNotificationToggleChange}
                        disabled={
                          notificationPreferences !== "browser" ||
                          browserNotificationEnabled
                        }
                        sx={{ ml: 2 }}
                        startIcon={
                          browserNotificationEnabled &&
                          notificationPreferences === "browser" ? (
                            <CheckCircleIcon />
                          ) : undefined
                        }
                      >
                        {notificationPreferences !== "browser"
                          ? t("Disabled")
                          : browserNotificationEnabled
                            ? t("Enabled")
                            : t("Request Permission")}
                      </Button>
                    </Box>
                    {notificationPreferences !== "browser" && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        {t("browserNotificationDisabledByPreference")}
                      </Alert>
                    )}
                    {showBrowserNotificationHint && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        {t("browserNotificationHint")}
                      </Alert>
                    )}
                  </FormControl>}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  type="submit"
                  color="success"
                  variant="contained"
                  sx={{ borderRadius: 10, mt: 2 }}
                >
                  {t("save")}
                </Button>
              </Box>
            </form>
          </>
        ) : (
          <Loader />
        )}
      </Paper>
    </Box>
  );
}

export default Profile;
